import {
  BUSINESS_UNIT_LABELS,
  LOYALTY_EARN_RULE_KIND_LABELS,
  LOYALTY_RULE_SCOPE_LABELS,
  type LoyaltyEarnRuleView,
} from "@station/contracts";
import { Decimal, formatTrDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  MoneyInput,
  Select,
} from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Calculator, Plus, Sparkles } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EarnRuleDialog } from "../../../features/loyalty/earn-rule-dialog.js";
import {
  earnRuleEffect,
  formatPoints,
  lineKindOfProduct,
  simulateEarn,
} from "../../../features/loyalty/loyalty-values.js";
import {
  useEarnRules,
  useLoyaltyPermissions,
  useLoyaltyProducts,
  useLoyaltyProgram,
  useProductCategories,
  useLoyaltyTiers,
} from "../../../features/loyalty/queries.js";
import { ActiveBadge, LoyaltyPage } from "../../../features/loyalty/shared.js";
import { DecimalInput } from "../../../features/invoicing/inputs.js";
import { validToDate, weekdaysText } from "../../../features/pos/pos-values.js";
import { errorMessage } from "../../../lib/errors.js";

export const Route = createFileRoute("/_app/sadakat/kurallar")({ component: EarnRulesRoute });

/**
 * Kazanım kuralları (ADR 0017 §20): liste + editör + "kazanım simülatörü". Simülatör kasa ve sunucunun kullandığı AYNI
 * motoru (`computeLoyaltyEarn`) kayıtlı kurallarla çalıştırır — yazma yapmaz.
 */
function EarnRulesRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useLoyaltyPermissions();
  const [editing, setEditing] = useState<{ rule: LoyaltyEarnRuleView | null } | null>(null);
  return (
    <LoyaltyPage
      title={t("menu.loyaltyEarnRules")}
      description={t("loyalty.rules.description")}
      actions={
        permissions.canManage ? (
          <Button onClick={() => setEditing({ rule: null })}>
            <Plus aria-hidden="true" />
            {t("loyalty.rules.create")}
          </Button>
        ) : null
      }
    >
      <RulesBody onEdit={(rule) => setEditing({ rule })} />
      <EarnRuleDialog
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        rule={editing?.rule ?? null}
      />
    </LoyaltyPage>
  );
}

function validityText(rule: LoyaltyEarnRuleView, openEnd: string): string {
  const from = formatTrDate(new Date(rule.validFrom));
  return rule.validTo ? `${from} – ${formatTrDate(validToDate(rule.validTo))}` : `${from} – ${openEnd}`;
}

function RulesBody({ onEdit }: { onEdit: (rule: LoyaltyEarnRuleView) => void }): ReactElement {
  const { t } = useTranslation();
  const permissions = useLoyaltyPermissions();
  const rules = useEarnRules();
  const products = useLoyaltyProducts();
  const rows = useMemo(
    () =>
      [...(rules.data ?? [])].sort(
        (a, b) =>
          Number(b.isActive) - Number(a.isActive) || a.priority - b.priority || a.code.localeCompare(b.code),
      ),
    [rules.data],
  );
  const categories = useProductCategories();
  // Kapsam kimlikleri okunur adlara çevrilir (ürün / kategori kataloğundan; bulunamayan kimlik olduğu gibi).
  const scopeNames = useMemo(
    () =>
      new Map([
        ...(products.data ?? []).map((product) => [product.id, product.name] as const),
        ...(categories.data ?? []).map((category) => [category.id, category.name] as const),
      ]),
    [products.data, categories.data],
  );

  const columns = useMemo<DataTableColumn<LoyaltyEarnRuleView>[]>(
    () => [
      {
        id: "code",
        header: t("loyalty.rules.fields.code"),
        hideable: false,
        exportValue: (row) => row.code,
        cell: (row) => <span className="font-mono">{row.code}</span>,
      },
      {
        id: "name",
        header: t("loyalty.rules.fields.name"),
        exportValue: (row) => row.name,
        cell: (row) => row.name,
      },
      {
        id: "kind",
        header: t("loyalty.rules.fields.kind"),
        exportValue: (row) => LOYALTY_EARN_RULE_KIND_LABELS[row.kind],
        cell: (row) => (
          <span className="grid">
            <span>{LOYALTY_EARN_RULE_KIND_LABELS[row.kind]}</span>
            <span className="tabular text-xs text-muted-foreground">{earnRuleEffect(row)}</span>
          </span>
        ),
      },
      {
        id: "scope",
        header: t("loyalty.rules.fields.scopeKind"),
        exportValue: (row) => LOYALTY_RULE_SCOPE_LABELS[row.scopeKind],
        cell: (row) => (
          <span className="grid max-w-72">
            <span>{LOYALTY_RULE_SCOPE_LABELS[row.scopeKind]}</span>
            {row.scopeIds.length > 0 ? (
              <span className="line-clamp-2 text-xs text-muted-foreground">
                {row.scopeIds
                  .map((id) =>
                    row.scopeKind === "BUSINESS_UNIT"
                      ? (BUSINESS_UNIT_LABELS[id as keyof typeof BUSINESS_UNIT_LABELS] ?? id)
                      : (scopeNames.get(id) ?? id),
                  )
                  .join(", ")}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        id: "priority",
        header: t("loyalty.rules.fields.priority"),
        align: "right",
        exportValue: (row) => row.priority,
        cell: (row) => <span className="tabular">{row.priority}</span>,
      },
      {
        id: "validity",
        header: t("loyalty.rules.validity"),
        exportValue: (row) => validityText(row, t("loyalty.rules.openEnded")),
        cell: (row) => (
          <span className="grid whitespace-nowrap">
            <span className="tabular">{validityText(row, t("loyalty.rules.openEnded"))}</span>
            <span className="text-xs text-muted-foreground">
              {weekdaysText(row.weekdays, t("loyalty.rules.everyDay"))}
            </span>
          </span>
        ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => (row.isActive ? t("common.active") : t("common.inactive")),
        cell: (row) => <ActiveBadge active={row.isActive} />,
      },
      {
        id: "actions",
        header: t("common.actions"),
        cell: (row) =>
          permissions.canManage ? (
            <Button variant="outline" size="sm" className="relative" onClick={() => onEdit(row)}>
              {t("common.edit")}
              <span className="sr-only"> — {row.code}</span>
            </Button>
          ) : null,
      },
    ],
    [t, permissions.canManage, onEdit, scopeNames],
  );

  return (
    <>
      <DataTable
        caption={t("loyalty.rules.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={rules.isPending}
        error={rules.error ? <Alert tone="danger">{errorMessage(rules.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Sparkles />}
            title={t("loyalty.rules.emptyTitle")}
            description={t("loyalty.rules.emptyDescription")}
          />
        }
      />
      <p className="text-xs text-muted-foreground">{t("loyalty.rules.footnote")}</p>
      <EarnSimulator rules={rules.data ?? []} />
    </>
  );
}

/**
 * Kazanım simülatörü: örnek satır (ürün, miktar, tutar) + seviye → kaç puan kazandırır, hangi kural uygulandı. Hesap
 * `@station/pricing-engine` `computeLoyaltyEarn` ile istemcide yapılır (kayıt yok).
 */
function EarnSimulator({ rules }: { rules: readonly LoyaltyEarnRuleView[] }): ReactElement {
  const { t } = useTranslation();
  const products = useLoyaltyProducts();
  const tiers = useLoyaltyTiers();
  const program = useLoyaltyProgram();
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState<string | null>("40");
  const [net, setNet] = useState<string | null>("200000");
  const [tierCode, setTierCode] = useState("");
  const product = (products.data ?? []).find((item) => item.id === productId) ?? null;
  const tier = (tiers.data ?? []).find((item) => item.code === tierCode) ?? null;
  const ready = product !== null && quantity !== null && net !== null && Decimal.from(quantity).isPositive();
  const result = ready
    ? simulateEarn({
        rules,
        kind: lineKindOfProduct(product.productType),
        productId: product.id,
        categoryId: product.categoryId,
        businessUnit: product.businessUnit,
        ageRestricted: product.isAgeRestricted,
        quantity: Decimal.from(quantity).toFixed(3),
        net,
        at: new Date(),
        tierMultiplier: tier?.earnMultiplier ?? null,
        excludeAgeRestricted: program.data?.excludeAgeRestricted ?? true,
      })
    : null;
  const ruleName = (id: string) => rules.find((rule) => rule.id === id)?.name ?? id;

  return (
    <Card className="grid gap-4 p-4">
      <div className="grid gap-1">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Calculator className="size-4" aria-hidden="true" />
          {t("loyalty.rules.simulator.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("loyalty.rules.simulator.description", { day: formatTrDate(toIstanbulDate(new Date())) })}
        </p>
      </div>
      <form
        aria-label={t("loyalty.rules.simulator.title")}
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(event) => event.preventDefault()}
      >
        <FormField label={t("loyalty.rules.simulator.product")}>
          {(control) => (
            <Select {...control} value={productId} onChange={(event) => setProductId(event.target.value)}>
              <option value="">{t("common.select")}</option>
              {(products.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.code} {item.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("loyalty.rules.simulator.quantity")}>
          {(control) => <DecimalInput {...control} scale={3} value={quantity} onValueChange={setQuantity} />}
        </FormField>
        <FormField label={t("loyalty.rules.simulator.net")}>
          {(control) => <MoneyInput {...control} value={net} onValueChange={setNet} />}
        </FormField>
        <FormField label={t("loyalty.rules.simulator.tier")}>
          {(control) => (
            <Select {...control} value={tierCode} onChange={(event) => setTierCode(event.target.value)}>
              <option value="">{t("loyalty.rules.simulator.noTier")}</option>
              {(tiers.data ?? [])
                .filter((item) => item.isActive)
                .map((item) => (
                  <option key={item.id} value={item.code}>
                    {item.name} (× {Decimal.from(item.earnMultiplier).formatTr(2)})
                  </option>
                ))}
            </Select>
          )}
        </FormField>
      </form>
      <div
        role="status"
        aria-live="polite"
        data-testid="earn-simulation"
        className="rounded-md border bg-muted/40 p-3 text-sm"
      >
        {!result ? (
          <span className="text-muted-foreground">{t("loyalty.rules.simulator.pick")}</span>
        ) : (
          <div className="grid gap-1">
            <p className="text-base font-semibold">
              {t("loyalty.rules.simulator.result", { points: formatPoints(result.points) })}
            </p>
            <p className="text-muted-foreground">
              {result.ruleId
                ? t("loyalty.rules.simulator.appliedRule", { name: ruleName(result.ruleId) })
                : t("loyalty.rules.simulator.noRule")}
              {result.multiplierRuleIds.length > 0
                ? ` · ${t("loyalty.rules.simulator.multipliers", {
                    names: result.multiplierRuleIds.map(ruleName).join(", "),
                  })}`
                : ""}
              {tier ? ` · ${t("loyalty.rules.simulator.tierApplied", { name: tier.name })}` : ""}
            </p>
            <p className="tabular text-xs text-muted-foreground">
              {t("loyalty.rules.simulator.raw", { raw: Decimal.from(result.rawPoints).formatTr(4) })}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
